import { EditorState, RichUtils } from 'draft-js'
import { getSelectionEntity } from 'draftjs-utils'
import React, { useCallback, useState } from 'react'
import ButtonContainer from 'components/ui/button-container'
import Popover from 'components/ui/popover2'
import ToggleButton from 'components/ui/toggle-button'
import LinkSettings from './LinkSettings'
import type { IconKey } from 'components/ui/icon'

type ToolbarButton = {
  label: string
  style: string
  icon?: IconKey
}
const INLINE_TYPES: ToolbarButton[] = [
  {
    label: 'Bold',
    style: 'BOLD',
    icon: 'bold',
  },
  {
    label: 'Italic',
    style: 'ITALIC',
    icon: 'italic',
  },
  {
    label: 'Underline',
    style: 'UNDERLINE',
    icon: 'underline',
  },
]

const getCurrentEntityType = (
  editorState: EditorState,
): string | null | undefined => {
  const entityKey = getSelectionEntity(editorState)

  if (entityKey) {
    const contentState = editorState.getCurrentContent()
    const entity = contentState.getEntity(entityKey)
    return entity.getType()
  }

  return null
}

const preventDefault = (event: React.SyntheticEvent<any>) =>
  event.preventDefault()

type Props = {
  disabled?: boolean
  editorState: EditorState
  onChange: (state: EditorState) => void
}

const Toolbar = ({
  disabled,
  editorState,
  onChange,
}: Props): React.ReactElement<'div'> => {
  const currentEntityType = getCurrentEntityType(editorState)
  const currentInlineStyle = editorState.getCurrentInlineStyle()
  const [isPopoverVisible, setPopoverVisible] = useState<boolean>(false)
  const hidePopover = useCallback(() => setPopoverVisible(false), [])
  const toggleInlineStyle = useCallback(
    (style: string) => {
      onChange((editorState: EditorState) => {
        return RichUtils.toggleInlineStyle(editorState, style)
      })
    },
    [onChange],
  )
  return (
    <div onMouseDown={preventDefault}>
      <ButtonContainer>
        {INLINE_TYPES.map((item, index) => (
          <ToggleButton
            caption={item.label}
            disabled={disabled}
            icon={item.icon}
            key={index}
            selected={currentInlineStyle.has(item.style)}
            value={item.style}
            onClick={toggleInlineStyle}
          />
        ))}
        <Popover
          activator={
            <ToggleButton
              caption="Add link"
              disabled={disabled}
              icon="link"
              selected={currentEntityType === 'LINK'}
              value={!isPopoverVisible}
              onClick={setPopoverVisible}
            />
          }
          content={
            <LinkSettings editorState={editorState} onChange={onChange} />
          }
          open={isPopoverVisible}
          onClose={hidePopover}
        />
      </ButtonContainer>
    </div>
  )
}

export default Toolbar
